import { useEffect, useState } from "react";

/* ═══════════════════════════════════════════════════════════
   Käyttäjän näyttötila palvelimella (170, Toni 25.9.2026).

   Ohjelaatikko ja valikkoryhmät auki/kiinni. Valinta kuuluu tunnukseen, ei laitteeseen:
   selaimen muistiin tallennettu valinta palaisi toisella laitteella. Haetaan kerran
   (/api/nayttotila), ja muutos lähetetään heti. Epäonnistunut haku = oletukset —
   näkymä ei riipu tästä.
   ═══════════════════════════════════════════════════════════ */

export type Laji = "ohje" | "valikko";
export type Tila = "auki" | "kiinni";
type Tilat = Record<Laji, Record<string, Tila>>;

let tilat: Tilat = { ohje: {}, valikko: {} };
let haku: Promise<void> | null = null;
const kuuntelijat = new Set<() => void>();
const ilmoita = () => kuuntelijat.forEach((f) => f());

// Epäonnistunut haku (esim. 401 ennen kirjautumista) ei jää välimuistiin: seuraava kutsu yrittää uudelleen.
function lataa(uudelleen = false): Promise<void> {
  if (!haku || uudelleen) {
    haku = fetch("/api/nayttotila", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: Tilat | null) => {
        if (j) { tilat = { ohje: j.ohje ?? {}, valikko: j.valikko ?? {} }; ilmoita(); } else haku = null;
      })
      .catch(() => { haku = null; });
  }
  return haku;
}

/** Tallennettu valinta tai `null` (= ei valintaa, käytä oletusta). */
export function useNayttotila(laji: Laji, tunnus: string): [Tila | null, (t: Tila) => void, boolean] {
  const [, setVersio] = useState(0);
  const [ladattu, setLadattu] = useState(false);
  useEffect(() => {
    const f = () => setVersio((v) => v + 1);
    kuuntelijat.add(f);
    lataa().then(() => setLadattu(true));
    return () => { kuuntelijat.delete(f); };
  }, []);
  const aseta = (t: Tila) => {
    tilat = { ...tilat, [laji]: { ...tilat[laji], [tunnus]: t } };
    ilmoita();
    fetch("/api/nayttotila", {
      method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ laji, tunnus, tila: t }),
    }).catch(() => {});
  };
  return [tilat[laji][tunnus] ?? null, aseta, ladattu];
}

/** Koko lajin tallennetut valinnat (esim. kaikki valikkoryhmät) ja asettaja. Kutsuttava ennen varhaisia paluita. */
export function useNayttotilat(laji: Laji, kayttajaId: number | null = null): [Record<string, Tila>, (tunnus: string, t: Tila) => void] {
  const [, setVersio] = useState(0);
  useEffect(() => {
    const f = () => setVersio((v) => v + 1);
    kuuntelijat.add(f);
    return () => { kuuntelijat.delete(f); };
  }, []);
  // Tunnuksen vaihtuessa (kirjautuminen, uloskirjautuminen) valinnat haetaan uudelleen.
  useEffect(() => {
    if (kayttajaId == null) { tilat = { ohje: {}, valikko: {} }; haku = null; ilmoita(); return; }
    void lataa(true);
  }, [kayttajaId]);
  const aseta = (tunnus: string, t: Tila) => {
    tilat = { ...tilat, [laji]: { ...tilat[laji], [tunnus]: t } };
    ilmoita();
    fetch("/api/nayttotila", {
      method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ laji, tunnus, tila: t }),
    }).catch(() => {});
  };
  return [tilat[laji], aseta];
}

/** Ryhmän otsikosta tallennettava tunnus: «Väki & kalusto» → «vaki_kalusto». */
export const ryhmanTunnus = (otsikko: string) =>
  otsikko.toLowerCase().replace(/[äå]/g, "a").replace(/ö/g, "o").replace(/[^a-z]+/g, "_").replace(/^_|_$/g, "").slice(0, 40);
